import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Globe,
  Server,
  FileText,
  Shield,
  ShieldCheck,
  ShieldX,
  Search,
  Copy,
  Check,
  ExternalLink,
  Download,
  Clock,
  RefreshCw,
  User,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// DNS Module Data Types
interface MxRecord {
  exchange: string;
  priority: number;
}

interface DnsRecords {
  a?: string[];
  aaaa?: string[];
  mx?: MxRecord[];
  ns?: string[];
  txt?: string[];
  cname?: string[];
  soa?: {
    mname?: string;
    rname?: string;
    serial?: number;
    refresh?: number;
    retry?: number;
    expire?: number;
    minimum?: number;
  };
  dnssec?: {
    enabled: boolean;
    validated?: boolean;
  };
}

interface Subdomain {
  subdomain: string;
  ips?: string[];
  sources?: string[];
}

export interface DnsModuleData {
  score?: number;
  target?: string;
  records?: DnsRecords;
  subdomains?: Subdomain[];
  issues?: Array<string | Issue>;
}

export interface DnsModuleViewProps {
  data: DnsModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Record type badge
function RecordTypeBadge({
  type,
  count,
}: {
  type: string;
  count: number;
}) {
  return (
    <Badge
      variant="outline"
      className="px-2 py-0.5 text-xs gap-1 bg-cyan-500/10 border-cyan-500/30 text-cyan-400"
    >
      {type.toUpperCase()}
      <span className="text-muted-foreground">({count})</span>
    </Badge>
  );
}

// Copyable value cell
function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group flex items-center gap-2">
      <span className="font-mono text-sm truncate">{value}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleCopy}
      >
        {copied ? (
          <Check className="h-3 w-3 text-emerald-400" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </Button>
    </div>
  );
}

// A/AAAA Records Section
function AddressRecordsSection({
  aRecords,
  aaaaRecords,
}: {
  aRecords?: string[];
  aaaaRecords?: string[];
}) {
  const allRecords = [
    ...(aRecords?.map((ip) => ({ ip, type: "A" as const })) || []),
    ...(aaaaRecords?.map((ip) => ({ ip, type: "AAAA" as const })) || []),
  ];

  if (allRecords.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No A/AAAA records found
      </p>
    );
  }

  const columns: ColumnDef<{ ip: string; type: "A" | "AAAA" }>[] = [
    {
      id: "type",
      header: "Type",
      accessor: (row) => row.type,
      cell: (value) => (
        <Badge
          variant="outline"
          className={cn(
            "text-xs",
            value === "A"
              ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400"
              : "bg-purple-500/10 border-purple-500/30 text-purple-400"
          )}
        >
          {value as string}
        </Badge>
      ),
      width: "80px",
      sortable: true,
      filterable: true,
    },
    {
      id: "ip",
      header: "IP Address",
      accessor: (row) => row.ip,
      cell: (value) => <CopyableValue value={value as string} />,
      sortable: true,
      filterable: true,
      copyable: true,
    },
    {
      id: "actions",
      header: "",
      accessor: (row) => row.ip,
      cell: (value) => (
        <a
          href={`https://ipinfo.io/${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-cyan-400 transition-colors"
        >
          Lookup
          <ExternalLink className="h-3 w-3" />
        </a>
      ),
      width: "80px",
      align: "right",
    },
  ];

  return (
    <ModuleDataTable
      data={allRecords}
      columns={columns}
      paginated={allRecords.length > 10}
      searchable={allRecords.length > 5}
      searchPlaceholder="Search IP addresses..."
      emptyMessage="No address records found"
    />
  );
}

// MX Records Section
function MxRecordsSection({ records }: { records?: MxRecord[] }) {
  if (!records || records.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No MX records found
      </p>
    );
  }

  const columns: ColumnDef<MxRecord>[] = [
    {
      id: "priority",
      header: "Priority",
      accessor: (row) => row.priority,
      cell: (value) => (
        <span className="font-mono text-cyan-400">{value as number}</span>
      ),
      width: "100px",
      sortable: true,
      align: "center",
    },
    {
      id: "exchange",
      header: "Mail Server",
      accessor: (row) => row.exchange,
      cell: (value) => <CopyableValue value={value as string} />,
      sortable: true,
      filterable: true,
      copyable: true,
    },
  ];

  const sortedRecords = [...records].sort((a, b) => a.priority - b.priority);

  return (
    <ModuleDataTable
      data={sortedRecords}
      columns={columns}
      paginated={false}
      searchable={false}
      emptyMessage="No MX records found"
    />
  );
}

// NS Records Section
function NsRecordsSection({ records }: { records?: string[] }) {
  if (!records || records.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No NS records found
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {records.map((ns, index) => (
        <div
          key={index}
          className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border"
        >
          <Server className="h-4 w-4 text-muted-foreground" />
          <CopyableValue value={ns} />
        </div>
      ))}
    </div>
  );
}

// TXT Records Section
function TxtRecordsSection({ records }: { records?: string[] }) {
  if (!records || records.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No TXT records found
      </p>
    );
  }

  // Categorize TXT records
  const categorized = records.map((record) => {
    let category = "other";
    let highlight = false;

    if (record.startsWith("v=spf1")) {
      category = "SPF";
      highlight = true;
    } else if (record.startsWith("v=DKIM1") || record.includes("DKIM")) {
      category = "DKIM";
      highlight = true;
    } else if (record.startsWith("v=DMARC1")) {
      category = "DMARC";
      highlight = true;
    } else if (record.includes("google-site-verification")) {
      category = "Google Verification";
    } else if (record.includes("MS=")) {
      category = "Microsoft Verification";
    } else if (record.includes("_")) {
      category = "Verification";
    }

    return { record, category, highlight };
  });

  return (
    <div className="space-y-3">
      {categorized.map((item, index) => (
        <div
          key={index}
          className={cn(
            "rounded-lg border overflow-hidden",
            item.highlight
              ? "border-cyan-500/30 bg-cyan-500/5"
              : "border-border bg-muted/30"
          )}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30">
            <FileText className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              {item.category}
            </span>
          </div>
          <pre className="p-3 text-xs font-mono text-foreground overflow-x-auto whitespace-pre-wrap break-all">
            {item.record}
          </pre>
        </div>
      ))}
    </div>
  );
}

// DNSSEC Status Section
function DnssecSection({
  dnssec,
}: {
  dnssec?: { enabled: boolean; validated?: boolean };
}) {
  const isEnabled = dnssec?.enabled ?? false;
  const isValidated = dnssec?.validated ?? false;

  return (
    <div
      className={cn(
        "flex items-center gap-4 p-4 rounded-lg border",
        isEnabled
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "border-amber-500/30 bg-amber-500/5"
      )}
    >
      {isEnabled ? (
        <ShieldCheck className="h-8 w-8 text-emerald-400" />
      ) : (
        <ShieldX className="h-8 w-8 text-amber-400" />
      )}
      <div>
        <h4
          className={cn(
            "font-semibold",
            isEnabled ? "text-emerald-400" : "text-amber-400"
          )}
        >
          DNSSEC {isEnabled ? "Enabled" : "Not Enabled"}
        </h4>
        <p className="text-sm text-muted-foreground">
          {isEnabled
            ? isValidated
              ? "DNS records are cryptographically signed and validated"
              : "DNS records are signed but validation status unknown"
            : "DNS records are not cryptographically signed"}
        </p>
      </div>
    </div>
  );
}

// Subdomains Section
function formatSourceName(source: string): string {
  const names: Record<string, string> = {
    ct_logs: "CT Logs",
    rapiddns: "RapidDNS",
    hackertarget: "HackerTarget",
    crtsh: "crt.sh",
    dns: "DNS",
    bruteforce: "Bruteforce",
    virustotal: "VirusTotal",
    censys: "Censys",
    shodan: "Shodan",
  };
  return names[source.toLowerCase()] || source;
}

function SubdomainsSection({ subdomains }: { subdomains?: Subdomain[] }) {
  if (!subdomains || subdomains.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No subdomains discovered
      </p>
    );
  }

  const columns: ColumnDef<Subdomain>[] = [
    {
      id: "subdomain",
      header: "Subdomain",
      accessor: (row) => row.subdomain,
      cell: (value) => <CopyableValue value={value as string} />,
      sortable: true,
      filterable: true,
      copyable: true,
    },
    {
      id: "ip",
      header: "IP Address",
      accessor: (row) => (row.ips && row.ips.length > 0 ? row.ips.join(", ") : ""),
      cell: (value) =>
        value ? (
          <span className="font-mono text-sm">{value as string}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      sortable: true,
      filterable: true,
    },
    {
      id: "status",
      header: "Status",
      accessor: (row) => (row.ips && row.ips.length > 0 ? "active" : "inactive"),
      cell: (value) => (
        <Badge
          variant="outline"
          className={cn(
            "text-xs",
            value === "active"
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
              : "bg-muted border-border text-muted-foreground"
          )}
        >
          {value === "active" ? "Active" : "No DNS"}
        </Badge>
      ),
      width: "100px",
      sortable: true,
    },
    {
      id: "source",
      header: "Source",
      accessor: (row) => (row.sources && row.sources.length > 0 ? row.sources.map(formatSourceName).join(", ") : ""),
      cell: (value) => (
        <span className="text-xs text-muted-foreground">
          {(value as string) || "—"}
        </span>
      ),
      width: "120px",
    },
  ];

  return (
    <ModuleDataTable
      data={subdomains}
      columns={columns}
      paginated={subdomains.length > 10}
      defaultPageSize={25}
      searchable
      searchPlaceholder="Search subdomains..."
      emptyMessage="No subdomains found"
    />
  );
}

// Convert string issues to Issue objects
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];

  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      // Parse severity from message if present
      let severity: Issue["severity"] = "info";
      const message = issue;

      if (
        message.toLowerCase().includes("critical") ||
        message.toLowerCase().includes("missing ns")
      ) {
        severity = "critical";
      } else if (
        message.toLowerCase().includes("high") ||
        message.toLowerCase().includes("dangling")
      ) {
        severity = "high";
      } else if (
        message.toLowerCase().includes("medium") ||
        message.toLowerCase().includes("warning")
      ) {
        severity = "medium";
      } else if (message.toLowerCase().includes("low")) {
        severity = "low";
      }

      return {
        id: `dns-issue-${index}`,
        severity,
        message,
        module: "dns",
      };
    }
    return issue;
  });
}

// Generate BIND-compatible zone file
function exportZoneFile(records: DnsRecords, domain: string) {
  const lines: string[] = [
    `; Zone file for ${domain}`,
    `; Generated by ASM Toolkit on ${new Date().toISOString()}`,
    `; BIND-compatible format`,
    "",
    `$ORIGIN ${domain}.`,
    `$TTL 3600`,
    "",
  ];

  // SOA record
  if (records.soa) {
    const soa = records.soa;
    lines.push(
      `@    IN    SOA    ${soa.mname || "ns1." + domain + "."} ${soa.rname || "hostmaster." + domain + "."} (`,
      `                  ${soa.serial || Math.floor(Date.now() / 1000)}    ; serial`,
      `                  ${soa.refresh || 7200}    ; refresh`,
      `                  ${soa.retry || 3600}    ; retry`,
      `                  ${soa.expire || 1209600}    ; expire`,
      `                  ${soa.minimum || 3600}    ; minimum TTL`,
      `                  )`,
      ""
    );
  }

  // NS records
  if (records.ns?.length) {
    lines.push("; Name servers");
    records.ns.forEach((ns) => {
      lines.push(`@    IN    NS    ${ns}`);
    });
    lines.push("");
  }

  // A records
  if (records.a?.length) {
    lines.push("; A records");
    records.a.forEach((ip) => {
      lines.push(`@    IN    A    ${ip}`);
    });
    lines.push("");
  }

  // AAAA records
  if (records.aaaa?.length) {
    lines.push("; AAAA records");
    records.aaaa.forEach((ip) => {
      lines.push(`@    IN    AAAA    ${ip}`);
    });
    lines.push("");
  }

  // MX records
  if (records.mx?.length) {
    lines.push("; MX records");
    records.mx.forEach((mx) => {
      lines.push(`@    IN    MX    ${mx.priority}    ${mx.exchange}`);
    });
    lines.push("");
  }

  // TXT records
  if (records.txt?.length) {
    lines.push("; TXT records");
    records.txt.forEach((txt) => {
      // Properly escape and wrap long TXT records
      const escapedTxt = txt.replace(/"/g, '\\"');
      if (escapedTxt.length <= 255) {
        lines.push(`@    IN    TXT    "${escapedTxt}"`);
      } else {
        // Split long TXT records
        const parts: string[] = [];
        for (let i = 0; i < escapedTxt.length; i += 255) {
          parts.push(`"${escapedTxt.slice(i, i + 255)}"`);
        }
        lines.push(`@    IN    TXT    ${parts.join(" ")}`);
      }
    });
    lines.push("");
  }

  // CNAME records
  if (records.cname?.length) {
    lines.push("; CNAME records");
    records.cname.forEach((cname) => {
      lines.push(`www    IN    CNAME    ${cname}`);
    });
    lines.push("");
  }

  const content = lines.join("\n");
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${domain.replace(/\./g, "_")}_zone.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Format seconds to human readable time
function formatSeconds(seconds?: number): string {
  if (!seconds) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return `${Math.floor(seconds / 604800)}w`;
}

// SOA Record Section
function SoaRecordSection({ soa }: { soa?: DnsRecords["soa"] }) {
  if (!soa) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No SOA record found
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Primary nameserver and admin */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-card/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
              <Server className="h-3 w-3" /> Primary Nameserver
            </CardTitle>
          </CardHeader>
          <CardContent>
            <code className="text-sm font-mono">{soa.mname || "Not set"}</code>
          </CardContent>
        </Card>
        <Card className="bg-card/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
              <User className="h-3 w-3" /> Admin Email (RNAME)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <code className="text-sm font-mono">
              {soa.rname?.replace(".", "@").replace(/\.$/, "") || "Not set"}
            </code>
          </CardContent>
        </Card>
      </div>

      {/* Timing parameters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="p-3 rounded-lg bg-muted/30 border border-border">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Serial</p>
          <p className="font-mono text-sm text-cyan-400">{soa.serial?.toLocaleString() || "—"}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/30 border border-border">
          <div className="flex items-center gap-1 mb-1">
            <RefreshCw className="h-2.5 w-2.5 text-muted-foreground" />
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Refresh</p>
          </div>
          <p className="font-mono text-sm">{formatSeconds(soa.refresh)}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/30 border border-border">
          <div className="flex items-center gap-1 mb-1">
            <RefreshCw className="h-2.5 w-2.5 text-muted-foreground" />
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Retry</p>
          </div>
          <p className="font-mono text-sm">{formatSeconds(soa.retry)}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/30 border border-border">
          <div className="flex items-center gap-1 mb-1">
            <Clock className="h-2.5 w-2.5 text-muted-foreground" />
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Expire</p>
          </div>
          <p className="font-mono text-sm">{formatSeconds(soa.expire)}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/30 border border-border">
          <div className="flex items-center gap-1 mb-1">
            <Clock className="h-2.5 w-2.5 text-muted-foreground" />
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Min TTL</p>
          </div>
          <p className="font-mono text-sm">{formatSeconds(soa.minimum)}</p>
        </div>
      </div>
    </div>
  );
}

export function DnsModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: DnsModuleViewProps) {
  const records = data.records || {};
  const issues = normalizeIssues(data.issues);

  // Count records by type
  const recordCounts = {
    a: records.a?.length || 0,
    aaaa: records.aaaa?.length || 0,
    mx: records.mx?.length || 0,
    ns: records.ns?.length || 0,
    txt: records.txt?.length || 0,
  };

  const totalRecords = Object.values(recordCounts).reduce((a, b) => a + b, 0);

  return (
    <ModuleContainer
      moduleName="dns"
      icon={Globe}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              Total Records
            </p>
            <p className="text-2xl font-bold font-mono text-foreground">
              {totalRecords}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              Subdomains
            </p>
            <p className="text-2xl font-bold font-mono text-foreground">
              {data.subdomains?.length || 0}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              DNSSEC
            </p>
            <p
              className={cn(
                "text-lg font-semibold",
                records.dnssec?.enabled ? "text-emerald-400" : "text-amber-400"
              )}
            >
              {records.dnssec?.enabled ? "Enabled" : "Disabled"}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              Issues
            </p>
            <p
              className={cn(
                "text-2xl font-bold font-mono",
                issues.length > 0 ? "text-amber-400" : "text-emerald-400"
              )}
            >
              {issues.length}
            </p>
          </div>
        </div>

        {/* Export Zone File */}
        {totalRecords > 0 && data.target && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportZoneFile(records, data.target!)}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Export Zone File
            </Button>
          </div>
        )}

        {/* DNS Records Tabs */}
        <ModuleSection
          title="DNS Records"
          icon={FileText}
          badge={
            <div className="flex gap-1">
              {recordCounts.a > 0 && (
                <RecordTypeBadge type="A" count={recordCounts.a} />
              )}
              {recordCounts.aaaa > 0 && (
                <RecordTypeBadge type="AAAA" count={recordCounts.aaaa} />
              )}
              {recordCounts.mx > 0 && (
                <RecordTypeBadge type="MX" count={recordCounts.mx} />
              )}
            </div>
          }
        >
          <Tabs defaultValue="address" className="space-y-4">
            <TabsList className="bg-muted/50 p-1">
              <TabsTrigger value="address" className="data-[state=active]:bg-background">
                A/AAAA
                {(recordCounts.a + recordCounts.aaaa) > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[10px]">
                    {recordCounts.a + recordCounts.aaaa}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="mx" className="data-[state=active]:bg-background">
                MX
                {recordCounts.mx > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[10px]">
                    {recordCounts.mx}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="ns" className="data-[state=active]:bg-background">
                NS
                {recordCounts.ns > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[10px]">
                    {recordCounts.ns}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="txt" className="data-[state=active]:bg-background">
                TXT
                {recordCounts.txt > 0 && (
                  <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[10px]">
                    {recordCounts.txt}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="soa" className="data-[state=active]:bg-background">
                SOA
              </TabsTrigger>
            </TabsList>

            <TabsContent value="address">
              <AddressRecordsSection
                aRecords={records.a}
                aaaaRecords={records.aaaa}
              />
            </TabsContent>

            <TabsContent value="mx">
              <MxRecordsSection records={records.mx} />
            </TabsContent>

            <TabsContent value="ns">
              <NsRecordsSection records={records.ns} />
            </TabsContent>

            <TabsContent value="txt">
              <TxtRecordsSection records={records.txt} />
            </TabsContent>

            <TabsContent value="soa">
              <SoaRecordSection soa={records.soa} />
            </TabsContent>
          </Tabs>
        </ModuleSection>

        {/* DNSSEC */}
        <ModuleSection title="DNSSEC Status" icon={Shield}>
          <DnssecSection dnssec={records.dnssec} />
        </ModuleSection>

        {/* Subdomains */}
        {data.subdomains && data.subdomains.length > 0 && (
          <ModuleSection
            title="Discovered Subdomains"
            icon={Search}
            badge={
              <Badge variant="outline" className="text-xs">
                {data.subdomains.length}
              </Badge>
            }
          >
            <SubdomainsSection subdomains={data.subdomains} />
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Security Issues"
            badge={
              <Badge
                variant="outline"
                className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
              >
                {issues.length}
              </Badge>
            }
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

DnsModuleView.displayName = "DnsModuleView";
